import { useEffect, useRef, useState } from 'react'
import { useT } from '@transifex/react'
import type { ApiCustomer } from '@vend/types'
import type { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import type { CustomerInfo, PartialCustomerGroup } from '../../../common/types'
import { trimContactInfo } from '../../../common/customer-contact-utils'
import {
  useCustomerManager,
  useSaleInventoryData,
  useSaleManager,
  useSaleTracker,
  useVdFeatureManager,
} from '../../../../../../ng-compat/hooks'
import { EVENT } from '../../../../../../constants'
import {
  selectLineItems,
  selectSaleCustomer,
  selectSaleFulfilmentDetail,
  selectSaleLocalId,
  setFulfillmentOutletID,
  setSaleFulfilmentDetail,
  updateLineItems,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  FulfilmentDetail,
  FulfilmentType,
  isNoteInvalid,
} from '../../../../../../models/fulfilment'
import {
  AddressErrorsStatus,
  useFulfillmentHelper,
} from '../../../../../sale/sale-line-item/fulfilment/popover/hooks/useFulfillmentHelper'
import { fillEmptyAddressWithCustomerAddress } from '../../../../../sale/sale-line-item/fulfilment/popover/utils/addressHelper'
import { openSaleAddressModal } from '../../../../../sale/sale-line-item/fulfilment/modals'
import { useCrossLocationFulfilmentGate } from '../../../../../sale/sale-line-item/fulfilment/OutletSelect/hooks/useCrossLocationFulfilmentGate'
import {
  wrToastNegative,
  wrToastSuccess,
} from '../../../../helios/ToastNotifications/WrToastNotifications'
import { STEPS } from '../types'
import type { Step3Props } from './Step3'

const CAN_NOT_SAVE_CUSTOMER = 'can not save customer'

const convertCustomerToApiCustomer = (
  customer: CustomerInfo | null,
  group: PartialCustomerGroup
) => {
  if (!customer) {
    return null
  }

  const result = {
    first_name: customer.firstName && customer.firstName.trim(),
    last_name: customer.lastName && customer.lastName.trim(),
    mobile: customer.mobile,
    email: customer.email,
    phone: customer.phone,
    fax: customer.fax,
    customer_group_id: group.id,
  } as ApiCustomer
  trimContactInfo(result)
  return result
}

export const useStep3Model = (props: Step3Props) => {
  const t = useT()
  const { validateFulfillmentAddresses } = useFulfillmentHelper()
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const saleInventoryData = useSaleInventoryData()
  const vdFeatureManager = useVdFeatureManager()
  const saleTracker = useSaleTracker()
  const dispatch = useAppDispatch()
  const saleFulfilmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const lineItems = useAppSelector(selectLineItems)
  const localId = useAppSelector(selectSaleLocalId)
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const { canUseCrossLocationFulfilment } = useCrossLocationFulfilmentGate()
  let customerCreated = false

  const initialFulfilmentDetail: FulfilmentDetail = props.selectedCustomer
    ? {
        ...saleFulfilmentDetail,
        ...fillEmptyAddressWithCustomerAddress(
          props.selectedCustomer,
          saleFulfilmentDetail?.billingAddress,
          saleFulfilmentDetail?.shippingAddress
        ),
      }
    : saleFulfilmentDetail

  const [localFulfilmentDetails, setLocalFulfillmentDetails] = useState<
    FulfilmentDetail[]
  >([initialFulfilmentDetail])
  const [loading, setLoading] = useState<boolean>(false)

  const shouldSaveSelectedOutletID = vdFeatureManager.get(
    'fulfill_from_another_outlet'
  )

  const markUnfulfilled = () => {
    if (props.type === FulfilmentType.DELIVERY) {
      saleManager.unfulfilledDelivery()
    } else {
      saleManager.unfulfilledPickUp()
    }
    saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)

    if (shouldSaveSelectedOutletID && props.fulfillmentOutletID) {
      dispatch(setFulfillmentOutletID(props.fulfillmentOutletID))
    }
  }

  const handleCustomerCreate = () => {
    return new Promise<void>((resolve, reject) => {
      const customer = convertCustomerToApiCustomer(
        props.newCustomer,
        props.group!
      )
      if (customer) {
        setLoading(true)
        return customerManager
          .saveNewCustomer(customer)
          .then(savedCustomer => {
            customerCreated = true
            return saleManager.addCustomer(savedCustomer)
          })
          .then(() => resolve())
          .catch(() => reject(CAN_NOT_SAVE_CUSTOMER))
      }
    })
  }

  const handleCustomerUpdate = () => {
    return new Promise<void>((resolve, reject) => {
      setLoading(true)
      if (props.contactChanged) {
        trimContactInfo(props.selectedCustomer!)
        return customerManager
          .updateCustomer(props.selectedCustomer!)
          .then(savedCustomer => saleManager.addCustomer(savedCustomer))
          .then(() => resolve())
          .catch(() => reject(CAN_NOT_SAVE_CUSTOMER))
      } else if (
        saleCustomer &&
        saleCustomer.id === props.selectedCustomer!.id
      ) {
        return resolve()
      } else {
        return saleManager
          .addCustomer(props.selectedCustomer!)
          .then(() => resolve())
      }
    })
  }

  const handleMark = (
    shippingAddress?: AddressDetails,
    billingAddress?: AddressDetails
  ) => {
    const customerPromise = props.selectedCustomer
      ? handleCustomerUpdate
      : handleCustomerCreate

    customerPromise()
      .then(() => {
        markUnfulfilled()
        dispatch(
          updateLineItems(
            lineItems.map(item => ({
              ...item,
              fulfilmentType: props.type,
              ...(canUseCrossLocationFulfilment && {
                fulfilmentOutletId: props.perLineFulfilmentOutletId,
              }),
            }))
          )
        )
        const newFulfillmentDetails = {
          type: props.type,
          note: props.note,
          billingAddress:
            billingAddress || localFulfilmentDetails?.[0]?.billingAddress,
          shippingAddress:
            shippingAddress || localFulfilmentDetails?.[0]?.shippingAddress,
        }

        dispatch(setSaleFulfilmentDetail(newFulfillmentDetails))

        saleManager.updateFulfillmentDetails([newFulfillmentDetails])

        saleManager.saveSale()
        saleTracker.addTrackingData({
          customerCreated,
          saleId: localId,
        })

        wrToastSuccess(
          props.type === FulfilmentType.DELIVERY
            ? 'mark-as-unfulfilled.delivery.success'
            : 'mark-as-unfulfilled.pickup.success',
          { totalQuantity: props.totalQuantity }
        )

        props.onClose(true)
      })
      .catch(error => {
        setLoading(false)
        if (error && error !== CAN_NOT_SAVE_CUSTOMER) {
          wrToastNegative('mark-as-unfulfilled.negative')
        }
      })
  }

  const handleSaveAddress = (fulfillmentDetails: FulfilmentDetail[]) => {
    setLocalFulfillmentDetails(fulfillmentDetails)
  }

  const handleBillingAddressForShippingAddress = () =>
    handleMark(localFulfilmentDetails?.[0]?.billingAddress)

  const handleSaveAddressAndMark = (fulfillmentDetails: FulfilmentDetail[]) => {
    handleSaveAddress(fulfillmentDetails)
    handleMark(
      fulfillmentDetails?.[0]?.shippingAddress,
      fulfillmentDetails?.[0]?.billingAddress
    )
  }

  const { error, status } = validateFulfillmentAddresses(
    localFulfilmentDetails?.[0]
  )

  const shouldShowFulfilmentAddress = props.type === FulfilmentType.DELIVERY

  const shouldAutoOpenAddressModal =
    shouldShowFulfilmentAddress &&
    status === AddressErrorsStatus.EMPTYSHIPPING_EMPTYBILLING

  const [addressModalPending, setAddressModalPending] = useState(
    shouldAutoOpenAddressModal
  )
  const addressModalOpenedRef = useRef(false)

  useEffect(() => {
    if (!shouldAutoOpenAddressModal || addressModalOpenedRef.current) return
    addressModalOpenedRef.current = true

    openSaleAddressModal({
      formType: 'add',
      fulfilmentDetails: localFulfilmentDetails?.[0],
      onClose: () => {
        setAddressModalPending(false)
      },
      onSave: (fulfillmentDetails: FulfilmentDetail[]) => {
        handleSaveAddress(fulfillmentDetails)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    addressModalOpenedRef,
    addressModalPending,
    backLabel: t('Back', {
      _context: 'Mark as unfulfilled modal - back button',
    }),
    detailDescription: t(
      '{totalQuantity, plural, one {Record the fulfillment details for the item in the fulfillment note.} other {Record the fulfillment details for these items in the fulfillment note.}}',
      {
        _context: 'Mark as unfulfilled modal - subheader',
        totalQuantity: props.totalQuantity,
      }
    ),
    detailTitle: t(
      '{totalQuantity, plural, one {Add fulfillment details for the {totalQuantity} item} other {Add fulfillment details for these {totalQuantity} items}}',
      {
        _context: 'Mark as unfulfilled modal - header',
        totalQuantity: props.totalQuantity,
      }
    ),
    handleBack: () =>
      props.onStepChange(props.newCustomer ? STEPS.ADD : STEPS.CUSTOMER),
    handleBillingAddressForShippingAddress,
    handleClose: () => props.onClose(false),
    handleMark,
    handleSaveAddress,
    handleSaveAddressAndMark,
    isMarkButtonDisabled:
      isNoteInvalid(props.note) ||
      (props.type === FulfilmentType.DELIVERY && Boolean(error)),
    isShippingAddressFixRequired:
      props.type === FulfilmentType.DELIVERY &&
      (status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
        status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING),
    loading,
    localFulfilmentDetail: localFulfilmentDetails[0],
    markButtonLabel: t('Mark items for fulfillment', {
      _context: 'Mark as unfulfilled modal - next button',
    }),
    shouldShowFulfilmentAddress,
  }
}
